import { apiClient } from "@/shared/api/apiClient";

// ---- Ortak alt tipler ----
export interface AnalyticsLive {
  online: number;
  inTest: number;
  inCourse: number;
}
export interface HardestQuestion {
  questionId: string;
  preview: string | null;
  courseName: string | null;
  /** Bir şık işaretleyen sayısı = doğru + yanlış. Boş bırakanlar sayılmaz. */
  answeredCount: number;
  /** Yanlış işaretleyen sayısı — oran gözle doğrulanabilsin diye taşınır. */
  wrongCount: number;
  /** wrongCount / answeredCount (%). */
  wrongPercent: number;
}
export interface TopTest {
  testName: string;
  topicName: string | null;
  participants: number;
  avgNet: number | null;
  avgSeconds: number | null;
}
export interface CoursePerformance {
  courseName: string;
  solved: number;
  accuracyPercent: number | null;
  /** Kurs-bazlı tamamlama saklanmıyor → backend her zaman null döndürür. */
  completionPercent: number | null;
}
export interface LowCreditSchool {
  schoolId: string;
  schoolName: string;
  remaining: number;
}
/**
 * Etkileşim serisinin bir kovası — "Etkileşim" sekmelerinin (aktif / edinim / çözülen soru) ortak
 * noktası. Kova genişliği seçime göre gün / hafta / ay olur; {@link day} kovanın BAŞLANGICIDIR
 * (hafta = Pazartesi, ay = ayın 1'i).
 */
export interface DailyPoint {
  /** ISO tarih (yyyy-MM-dd) — kovanın başlangıcı. */
  day: string;
  /** Kova içinde en az bir soru çözen benzersiz öğrenci (aylık değer günlüklerin toplamı DEĞİLDİR). */
  activeStudents: number;
  /** O kovada bu yayınevinin öğrencisi olan benzersiz öğrenci sayısı (ilk giriş + ilk atama). */
  newStudents: number;
  solvedQuestions: number;
}

/** "Etkileşim" kartının kırılımı: gün (90), hafta (26) veya ay (12). */
export type EngagementGranularity = "DAY" | "WEEK" | "MONTH";
export interface UpcomingExamLite {
  examBookId: string;
  bookName: string;
  packageName: string | null;
  publishAt: string;
}

// ---- /overview ----
export interface AnalyticsOverview {
  publisherName: string;
  hasLogo: boolean;
  live: AnalyticsLive;
  /** `total` = yayınevine bugüne kadar dahil olmuş benzersiz öğrenci ("bu ay aktif / toplam" paydası). */
  activeStudents: { thisMonth: number; lastMonth: number; total: number };
  questionsSolved: { total: number; thisMonth: number };
  accuracyPercent: number | null;
  avgNet: number | null;
  completionPercent: number | null;
  schoolCount: number;
  newSchoolsThisMonth: number;
  revenue: { total: number; thisMonth: number; lastMonth: number };
  credits: { granted: number; used: number };
  totalPackages: number;
  packagesGranted: number;
  assignedStudentCount: number;
  activeTrend: number[];
  solvedTrend: number[];
  /** Son 90 gün, kronolojik — pano "Etkileşim" sekmeleri. */
  engagementTrend: DailyPoint[];
  hardestQuestions: HardestQuestion[];
  topTests: TopTest[];
  coursePerformance: CoursePerformance[];
  lowCreditSchools: LowCreditSchool[];
  inactiveSchoolCount: number;
  upcomingExams: UpcomingExamLite[];
  /** Yayınevinin en az bir AI kullanım kaydı var mı — "AI Kullanımı" menüsü koşulu. */
  hasAiUsage: boolean;
  /** Yayınevinin yapay zekâ yetkisi açık mı — AI'ya özel gösterimlerin koşulu (useAiEnabled). */
  aiEnabled: boolean;
  /** Maskot yükleme özelliği açık mı — "Maskotlar" menüsü/sayfası koşulu. */
  mascotUploadEnabled: boolean;
  /** Optik Form özelliği açık mı — "Optik Denemeler" menüsü/sayfası koşulu. */
  opticalFormEnabled: boolean;
  /** Video linki olan video-çözüm düğüm sayısı — sol menü "Video Çözümler" rozeti (>0 ise). */
  videoSolutionsCount: number;
  /** En az bir uygulama-içi satış kaydı var mı — sol menü "Satışlar" öğesi koşulu. */
  hasSales: boolean;
}

// ---- /detailed ----
export interface FunnelData {
  assigned: number;
  opened: number;
  attempted: number;
  completed70: number;
  completed100: number;
}
export interface StreakBucket {
  bucket: string;
  count: number;
}
export interface AbandonedTopic {
  topicName: string;
  courseName: string | null;
  dropPercent: number;
}
export interface SchoolRow {
  schoolId: string;
  schoolName: string;
  province: string | null;
  studentCount: number;
  activePercent: number | null;
  avgNet: number | null;
  /** Başarı oranı = doğru ÷ cevaplanan (%); "En başarılı okullar" sıralama/gösterim metriği. */
  successPercent: number | null;
}
export interface ProvinceRow {
  province: string;
  studentCount: number;
}
export interface AdoptionPoint {
  month: string;
  activeSchools: number;
  grantedCredits: number;
}
export interface ExamAnalytics {
  publishedCount: number;
  totalParticipants: number;
  avgNet: number | null;
  netDistribution: Array<{ bucket: string; count: number }>;
  topSchools: Array<{ schoolName: string; participants: number; avgNet: number | null; maxNet: number | null }>;
}
export interface AnalyticsDetailed {
  funnel: FunnelData;
  streaks: StreakBucket[];
  courseCompletion: CoursePerformance[];
  abandonedTopics?: AbandonedTopic[];
  schools: SchoolRow[];
  provinceDistribution: ProvinceRow[];
  adoptionTrend: AdoptionPoint[];
  exams: ExamAnalytics;
}

// ---- /hardest-questions (Zorlanılan Sorular sayfası) ----
export interface HardestQuestionRow {
  questionId: string;
  preview: string | null;
  topicName: string | null;
  bookName: string | null;
  /** Bir şık işaretleyen sayısı = doğru + yanlış (yanlış oranının paydası). Boşlar sayılmaz. */
  answeredCount: number;
  wrongCount: number;
  wrongPercent: number;
}
export interface CourseHardestQuestions {
  courseId: string | null;
  courseName: string | null;
  /** O derste eşiği geçen TÜM soru sayısı (liste ders başına 50 ile kırpılır). */
  totalQuestions: number;
  questions: HardestQuestionRow[];
}

// ---- /question-answer-stats (önizlemedeki şık dağılımı) ----
export interface AnswerOptionCount {
  label: string;
  /** O şıkkı işaretleyen öğrenci sayısı. */
  count: number;
  /** Şıkkın işaretleyenler içindeki payı (payda answeredCount). */
  percent: number;
}
export interface QuestionAnswerStats {
  questionId: string;
  /** Bir şık işaretleyen sayısı (yüzdelerin paydası) — listedeki "Cevap" kolonuyla aynı. */
  answeredCount: number;
  /** Doğru şık dışında işaretleyenlerin oranı — listedeki "Yanlış oranı" ile aynı tanım. */
  wrongPercent: number;
  /** En çok işaretlenen şık (eşitlikte alfabetik ilk). */
  topOption: string | null;
  /**
   * Ortalama işaretleme süresi (sn). Yalnız süreyi ölçen kaynaklarda (Günün Sorusu) dolar; konu
   * testi dağılımında süre soru bazında ölçülmediği için gelmez.
   */
  avgSeconds?: number | null;
  options: AnswerOptionCount[];
}

// ---- /app-usage (Uygulama Analizi sayfası) ----
export interface AppUsagePulse {
  dau: number;
  wau: number;
  mau: number;
  totalStudents: number;
  /** DAU ÷ MAU (%). Günlük alışkanlık mı, ayda bir uğrama mı. */
  stickiness: number | null;
  /** Son 4 haftada aktif öğrenci başına ortalama aktif gün (0-7). */
  avgDaysPerWeek: number | null;
  activeSchools: number;
  silentSchools: number;
}
export interface CohortRow {
  /** Kohortun ilk günü (haftanın başı, ISO Pazartesi). */
  cohortWeek: string;
  size: number;
  /** i. eleman = i. hafta geri dönüş %'si; henüz gelmemiş hafta null. */
  weeks: Array<number | null>;
}
export interface SurvivalPoint {
  day: number;
  percent: number;
}
export interface ChurnSchool {
  schoolId: string;
  schoolName: string;
  studentCount: number;
  idleCount: number;
  idlePercent: number | null;
}
export interface AppUsageChurn {
  idle7: number;
  idle14: number;
  idle30: number;
  topSchools: ChurnSchool[];
}
/** Daralan kümeler: assigned ≥ opened ≥ onboarded ≥ solved. */
export interface Activation {
  assigned: number;
  /** Uygulamayı en az bir kez açmış (profil satırı oluşmuş). */
  opened: number;
  /** Kayıt adımlarını tamamlamış. */
  onboarded: number;
  /** En az bir soru cevaplamış. */
  solved: number;
}
/** Son 28 günün aktif gün sayısına göre; dördünün toplamı öğrenci sayısına eşittir. */
export interface Regularity {
  regular: number;
  occasional: number;
  trial: number;
  inactive: number;
}
export interface HourCell {
  /** ISO gün: 1 = Pazartesi. */
  dow: number;
  hour: number;
  sessions: number;
}
export interface TopicTrend {
  topicId: string;
  topicName: string;
  courseName: string | null;
  /** Aynı konu adı farklı kitaplarda tekrar edebilir; ayrımı bu sağlar. */
  bookName: string | null;
  recentSessions: number;
  previousSessions: number;
  /** null = önceki dönemde hiç oturum yok (yeni ilgi gören konu). */
  changePercent: number | null;
}
export interface DeadTopic {
  topicId: string;
  topicName: string;
  bookName: string | null;
  courseName: string | null;
}
export interface AppUsageContent {
  rising: TopicTrend[];
  falling: TopicTrend[];
  dead: DeadTopic[];
  deadTotal: number;
  topicTotal: number;
}
export interface SchoolHealth {
  schoolId: string;
  schoolName: string;
  province: string | null;
  studentCount: number;
  activePercent: number | null;
  prevActivePercent: number | null;
  idle14Count: number;
  risk: "OK" | "WATCH" | "CRITICAL";
}
export interface AppUsage {
  pulse: AppUsagePulse;
  activation: Activation;
  regularity: Regularity;
  retention: { cohorts: CohortRow[]; survival: SurvivalPoint[] };
  churn: AppUsageChurn;
  hourHeatmap: HourCell[];
  content: AppUsageContent;
  schoolHealth: SchoolHealth[];
  /** Giriş-günü verisinin güvenilir olmaya başladığı tarih; öncesi alt sınırdır. */
  activityDataSince: string | null;
}

const BASE = "/publisher-admin/analytics";

export const analyticsApi = {
  overview: () => apiClient.get<AnalyticsOverview>(`${BASE}/overview`).then((r) => r.data),
  /** Gün serisi zaten overview içinde gelir; bu uç yalnız Hafta/Ay kırılımı için çağrılır. */
  engagement: (granularity: EngagementGranularity) =>
    apiClient.get<DailyPoint[]>(`${BASE}/engagement`, { params: { granularity } }).then((r) => r.data),
  detailed: (range = 30) =>
    apiClient.get<AnalyticsDetailed>(`${BASE}/detailed`, { params: { range } }).then((r) => r.data),
  appUsage: () => apiClient.get<AppUsage>(`${BASE}/app-usage`).then((r) => r.data),
  hardestQuestions: () =>
    apiClient.get<CourseHardestQuestions[]>(`${BASE}/hardest-questions`).then((r) => r.data),
  questionAnswerStats: (questionIds: string[]) =>
    apiClient
      .get<QuestionAnswerStats[]>(`${BASE}/question-answer-stats`, {
        params: { questionIds: questionIds.join(",") },
      })
      .then((r) => r.data),
};
